/** Hub SignalR ASP.NET (playground); fijo, no configurable. */
export const SANDBOX_SIGNALR_HUB_URL = "https://hub2.avicrm.es/aircall";

/** Rutas Nest: GET …/account/token/:loginAs — loginAs = email (tal cual llega al IdP). */
export const TOKEN_ROUTE_BASE =
  "/00000000-0000-0000-0000-000000000000/api/all/v2/account/token";

export type MonitorController = {
  destroy(): Promise<void>;
};

export type MonitorCreateFn = (
  opts: {
    container: HTMLElement | string;
    hubUrl: string;
    hubOptions?: Record<string, boolean>;
    tokenPath?: string;
  },
) => MonitorController;

export function getMonitor(): MonitorCreateFn | null {
  const W = window as typeof window & {
    AircallMonitor?: {
      create: MonitorCreateFn;
    };
  };
  return typeof W.AircallMonitor?.create === "function"
    ? W.AircallMonitor.create.bind(W.AircallMonitor)
    : null;
}

/** Una dirección por línea. */
export function parseEmails(raw: string): { key: string; display: string }[] {
  const seen = new Set<string>();
  const out: { key: string; display: string }[] = [];
  for (const line of raw.split(/\r?\n/u)) {
    const display = line.trim();
    if (display.length === 0) continue;
    const key = display.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ key, display });
  }
  return out;
}

export function getSignalRGlob(): unknown {
  return (window as Window & { signalR?: unknown }).signalR;
}

export function readHubUrl(): string {
  return SANDBOX_SIGNALR_HUB_URL;
}

/**
 * IDs únicos DOM. `crypto.randomUUID()` falla fuera de contexto seguro
 * (solo https, localhost, 127.0.0.1). Con hosts → http://algo.avicrm.es:3000
 * el navegador no es «secure» y hay que tener fallback.
 */
export function uniqueDomSuffix(): string {
  try {
    const c = globalThis.crypto;
    if (c?.randomUUID) return c.randomUUID();
  } catch {
    /* NotAllowedError u otro */
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

export function checkRuntimeReady():
  | { ok: true; create: MonitorCreateFn; hubUrl: string }
  | { ok: false; reason: string } {
  const create = getMonitor();
  if (!create || getSignalRGlob() === undefined) {
    return {
      ok: false,
      reason:
        "signalR y AircallMonitor deben estar cargados. Orden en index: signalr.min.js → aircall-monitor.js → bundle.",
    };
  }
  return { ok: true, create, hubUrl: readHubUrl() };
}

const OIDC_LOGIN = "/auth/oidc/login";

/** Respuesta JSON de `GET /auth/me` (Nest + OIDC). */
export type BffMeResponse = {
  user: string | null;
  sub?: string;
  email?: string;
  name?: string;
};

/**
 * Obtiene los datos de sesión OIDC vía BFF (`GET /auth/me`). Misma lógica de reintento que `checkBffSession`.
 */
export async function fetchBffMe(): Promise<BffMeResponse | null> {
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const ctrl = new AbortController();
      const timer = window.setTimeout(() => {
        ctrl.abort();
      }, 25_000);
      const r = await fetch("/auth/me", {
        credentials: "include",
        cache: "no-store",
        signal: ctrl.signal,
        headers: { Accept: "application/json" },
      });
      window.clearTimeout(timer);

      if (r.ok) {
        return (await r.json()) as BffMeResponse;
      }
      if (
        (r.status === 401 || r.status === 403 || !r.ok) &&
        attempt === 0
      ) {
        await new Promise((res) => setTimeout(res, 300));
        continue;
      }
      return null;
    } catch {
      if (attempt === 0) {
        await new Promise((res) => setTimeout(res, 300));
        continue;
      }
      return null;
    }
  }
  return null;
}

export function getOidcLoginUrl(): string {
  return new URL(OIDC_LOGIN, window.location.origin).href;
}

/** Sustituye la página actual (solo donde no importe perder estado en React). */
export function redirectToOidcLogin(): void {
  window.location.assign(getOidcLoginUrl());
}

/** Misma URL que `redirectToOidcLogin`, en otra pestaña: no se pierden widgets del bundle actual. Tras el login, las cookies aplican a este origen. */
export function openOidcLoginNewTab(): Window | null {
  return window.open(getOidcLoginUrl(), "_blank", "noopener,noreferrer");
}

/**
 * Comprueba sesión OIDC vía BFF (`GET /auth/me`). Ejecuta el middleware Nest y, si hace falta,
 * el refresh del access token. Llamar antes de crear widgets o rutas que exijan cookies.
 */
export async function checkBffSession(): Promise<boolean> {
  return (await fetchBffMe()) !== null;
}

/** Comprueba sesión BFF; si no hay usuario, navega al login OIDC en el mismo origen. */
export async function ensureSandboxSession(): Promise<boolean> {
  const ok = await checkBffSession();
  if (!ok) redirectToOidcLogin();
  return ok;
}
